import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

const InlineChatChart = ({ data, chartType, title, description, chartConfig }) => {
  console.log('Chart Debug:', { chartType, dataLength: data?.length, sampleData: data?.slice(0, 2) });
  
  if (!data || data.length === 0) {
    return (
      <Paper elevation={1} sx={{ p: 2, mt: 2, backgroundColor: 'background.secondary' }}>
        <Typography variant="body2" color="text.secondary">
          No data available for chart
        </Typography>
      </Paper>
    );
  }

  const renderChart = () => {
    switch (chartType) {
      case 'heatmap':
      case 'sparklines':
        // Multiple sparklines showing trends for different SKUs/attributes
        console.log('Rendering sparkline/heatmap chart with data:', data);
        
        if (!data || data.length === 0) {
          return <Typography variant="body2" color="text.secondary">No data for visualization</Typography>;
        }
        
        return (
          <Box sx={{ width: '100%', py: 1 }}>
            {data.map((item, idx) => (
              <Box key={idx} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" sx={{ minWidth: 140, fontSize: '0.75rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.label || item.x}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  {item.values && item.values.length > 0 ? (
                    <svg width="100%" height={30} style={{ display: 'block' }}>
                      <polyline
                        fill="none"
                        stroke={item.trend === 'falling' ? '#f44336' : item.trend === 'rising' ? '#4caf50' : '#47BDC6'}
                        strokeWidth="2"
                        points={item.values.map((val, i) => {
                          const x = (i / (item.values.length - 1)) * 100;
                          const min = Math.min(...item.values);
                          const max = Math.max(...item.values);
                          const range = max - min || 1;
                          const y = 30 - ((val - min) / range) * 25;
                          return `${x}%,${y}`;
                        }).join(' ')}
                      />
                    </svg>
                  ) : (
                    <Box sx={{ height: 8, backgroundColor: '#47BDC6', borderRadius: 1, width: `${(item.value || item.y) / (Math.max(...data.map(d => d.value || d.y || 1))) * 100}%` }} />
                  )}
                </Box>
                <Typography variant="caption" sx={{ minWidth: 60, textAlign: 'right', fontSize: '0.75rem' }}>
                  {item.current || item.value || item.values?.[item.values.length - 1] || ''}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      
      case 'grouped_bar':
        // Grouped bar chart for dual metrics
        console.log('Rendering grouped bar chart with data:', data);
        const metrics = chartConfig?.metrics || [];
        const xKey = chartConfig?.xKey || 'x';
        
        return (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart 
              data={data}
              margin={{ top: 5, right: 5, left: 5, bottom: 50 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey={xKey}
                tick={{ fontSize: 9 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tick={{ fontSize: 10 }}
              />
              <Tooltip />
              {metrics.map((metric, idx) => (
                <Bar 
                  key={metric}
                  dataKey={metric}
                  fill={idx === 0 ? '#47BDC6' : '#845BA3'}
                  stroke={idx === 0 ? '#2A8A91' : '#6B4582'}
                  strokeWidth={1}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="x" 
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line 
                type="monotone" 
                dataKey="y" 
                stroke="#47BDC6" 
                strokeWidth={2}
                dot={{ fill: '#47BDC6', strokeWidth: 2, r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="x" 
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar 
                dataKey="y" 
                fill="#845BA3"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'horizontal_bar':
        console.log('Rendering horizontal bar chart with data:', data);
        console.log('First item:', data[0]);
        
        // Ensure data is valid and has proper structure
        const validData = data.filter(item => {
          const hasX = item.x !== undefined && item.x !== null;
          const hasY = item.y !== undefined && item.y !== null && !isNaN(parseFloat(item.y));
          console.log(`Item validation: x=${item.x}, y=${item.y}, hasX=${hasX}, hasY=${hasY}`);
          return hasX && hasY;
        });
        console.log('Valid data for chart:', validData.length, 'items out of', data.length);
        
        if (validData.length === 0) {
          console.error('No valid data! Sample item:', data[0]);
          return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="error">
                Chart data validation failed. Check console for details.
              </Typography>
            </Box>
          );
        }
        
        // Debug the actual values and try vertical bar chart instead
        const minValue = Math.min(...validData.map(d => d.y));
        const maxValue = Math.max(...validData.map(d => d.y));
        console.log(`Chart range: ${minValue} to ${maxValue}`);
        
        return (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart 
              data={validData.slice(0, 15)} 
              margin={{ top: 5, right: 5, left: 5, bottom: 50 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="x" 
                tick={{ fontSize: 9 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tick={{ fontSize: 10 }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip 
                formatter={(value, name) => [`$${value.toLocaleString()}`, 'Revenue']}
                labelFormatter={(label) => `SKU: ${label}`}
              />
              <Bar 
                dataKey="y" 
                fill="#47BDC6"
                stroke="#2A8A91"
                strokeWidth={1}
              />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'heatmap_grid':
        // Heatmap grid for attribute combinations
        const xAxis = chartConfig?.xAxis || [];
        const heatmapMax = Math.max(...data.flatMap(row => xAxis.map(col => row[col] || 0)).filter(v => v > 0));
        
        return (
          <Box sx={{ 
            width: '100%', 
            overflowX: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            backgroundColor: '#fafafa',
            p: 1,
            mb: 2
          }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse', 
              fontSize: '0.75rem'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f0f0f0' }}>
                  <th style={{ 
                    padding: '10px 12px', 
                    textAlign: 'left', 
                    fontWeight: 700, 
                    borderBottom: '2px solid #47BDC6',
                    color: '#333'
                  }}>
                    {chartConfig?.description?.split('Rows = ')[1]?.split(',')[0] || 'Attribute'}
                  </th>
                  {xAxis.map((col, idx) => (
                    <th key={idx} style={{ 
                      padding: '10px 8px', 
                      textAlign: 'center', 
                      fontWeight: 700, 
                      borderBottom: '2px solid #47BDC6',
                      color: '#333',
                      fontSize: '0.7rem',
                      maxWidth: '80px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, rowIdx) => (
                  <tr key={rowIdx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td style={{ 
                      padding: '10px 12px', 
                      fontWeight: 600, 
                      fontSize: '0.75rem',
                      color: '#333',
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      backgroundColor: '#f9f9f9'
                    }}>
                      {row.name}
                    </td>
                    {xAxis.map((col, colIdx) => {
                      const value = row[col] || 0;
                      const intensity = value > 0 ? (value / heatmapMax) : 0;
                      const bgColor = intensity > 0 ? `rgba(71, 189, 198, ${Math.max(0.2, intensity * 0.9)})` : 'transparent';
                      const textColor = intensity > heatmapMax * 0.5 ? 'white' : '#333';
                      return (
                        <td key={colIdx} style={{ 
                          padding: '10px 8px', 
                          textAlign: 'center',
                          backgroundColor: bgColor,
                          color: textColor,
                          fontWeight: value > 0 ? 600 : 400,
                          fontSize: '0.75rem',
                          transition: 'background-color 0.2s ease'
                        }}>
                          {value > 0 ? value.toFixed(1) : '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        );
      
      case 'pie':
        const COLORS = ['#47BDC6', '#845BA3', '#EBDD71', '#FF6B6B', '#4ECDC4', '#45B7D1'];
        return (
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={data}
                dataKey="y"
                nameKey="x"
                cx="50%"
                cy="50%"
                outerRadius={60}
                paddingAngle={2}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default:
        return (
          <Typography variant="body2" color="text.secondary">
            Unsupported chart type: {chartType}
          </Typography>
        );
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {title && (
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5, fontSize: '0.75rem', display: 'block' }}>
          📊 {title}
        </Typography>
      )}
      
      <Box sx={{ width: '100%' }}>
        {renderChart()}
      </Box>
      
      {/* Compact data summary */}
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.5, display: 'block' }}>
        📈 {data.length} data points
      </Typography>
    </Box>
  );
};

export default InlineChatChart;
